import {
  Dispatch,
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  AuthTransaction,
  StatusEnum,
} from "pages/register/types/authTransaction";
import { ParsedFactorData } from "pages/register/types/parsedTransactionFactorData";

import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";

import {
  saveStateToken,
  deleteStateToken,
} from "pages/register/util/stateTokenManager";
import {
  getRegistrationData,
  saveRegistrationData,
  deleteRegistrationData,
} from "pages/register/util/registrationDataManager";
import { parseTransactionFactorList } from "pages/register/util/factorParser";
import oktaLogoutUrl from "pages/register/util/oktaLogoutUrl";
import useTransactionLoader from "./hooks/useTransactionLoader";
import { getFeatureFlags } from "hooks/useGetFeatureFlags";

const AuthTransactionProvider = ({
  children,
}: {
  children: ReactElement;
}): ReactElement => {
  const [transaction, setTransaction] = useState<AuthTransaction>();
  const [isTransactionExpired, setIsTransactionExpired] = useState(false);
  const [factorData, setFactorData] = useState<ParsedFactorData>(
    getRegistrationData().factorData || {
      factorMap: {},
      factorOrder: [],
    },
  );
  const {
    transaction: fetchedTransaction,
    isLoading: isFetchingTransaction,
    error: fetchTransactionError,
  } = useTransactionLoader();

  const _setTransaction: Dispatch<AuthTransaction> = useCallback(
    (transaction) => {
      if (!transaction) return;

      if (transaction.data.stateToken) {
        saveStateToken(transaction.data.stateToken);
      }

      if (transaction.status === StatusEnum.MfaEnroll) {
        const factorData = parseTransactionFactorList(transaction);

        saveRegistrationData({ factorData });
        setFactorData({
          ...factorData,
        });
      }

      if (transaction.status === StatusEnum.MfaEnrollActivate) {
        setFactorData((currentData) => {
          return {
            ...currentData,
          };
        });
      }

      if (transaction.status === StatusEnum.Success) {
        saveRegistrationData({
          sessionToken: transaction.sessionToken,
        });

        // When the transaction reaches "SUCCESS", the stateToken will no longer be valid
        deleteStateToken();
      }

      setTransaction(transaction);
    },
    [],
  );

  const completeRegistration = async (authorizationUrl: string) => {
    deleteStateToken();
    deleteRegistrationData();

    // TODO: Add functionality to delete the single logout cookie.
    // It's an HttpOnly cookie, so it can't be deleted from the client side.

    const featureFlags = await getFeatureFlags();
    const url = featureFlags.ciam_enable_pim_user_registration_logout
      ? oktaLogoutUrl(authorizationUrl)
      : authorizationUrl;

    window.location.href = url;
  };

  useEffect(() => {
    if (fetchedTransaction) {
      _setTransaction(fetchedTransaction);
    }
  }, [fetchedTransaction, _setTransaction]);

  useEffect(() => {
    if (fetchTransactionError) {
      setIsTransactionExpired(true);
    }
  }, [fetchTransactionError]);

  return (
    <AuthTransactionContext.Provider
      value={{
        isTransactionExpired,
        isFetchingTransaction,
        transaction,
        setTransaction: _setTransaction,
        setIsTransactionExpired,
        completeRegistration,
        factorData,
        setFactorData,
      }}
    >
      {children}
    </AuthTransactionContext.Provider>
  );
};

export default AuthTransactionProvider;
